import type { PlanTier } from '@/stores/subscriptionStore'

/**
 * Feature-to-minimum-tier mapping — used ONLY for UI labels ("requires Scale plan").
 * Access decisions use the backend-supplied plan.features array, not this map.
 *
 * KEEP IN SYNC with apps/api/config/plans.php tiers.*.features
 * If you add or move a feature in plans.php, update this map too.
 */
const FEATURE_TIER_MAP: Record<string, PlanTier> = {
  // Grow features (available to all tiers)
  flows: 'grow',
  questions: 'grow',
  segments: 'grow',
  signals: 'grow',
  widgets: 'grow',
  offers: 'grow',
  forms: 'grow',
  phrases: 'grow',
  basic_analytics: 'grow',
  mcp_access: 'grow',
  // Scale features
  campaigns: 'scale',
  hosted_pages: 'scale',
  csv_exports: 'scale',
  api_access: 'scale',
  full_analytics: 'scale',
  goals: 'scale',
  personalized_emails: 'scale',
  // Pro features
  account_intelligence: 'pro',
  people_pages: 'pro',
  webhooks: 'pro',
  split_testing: 'pro',
}

export function useFeatureGating() {
  const subscriptionStore = useSubscriptionStore()

  /** Check if the user can access a specific feature */
  const canAccessFeature = (feature: string): boolean => {
    return subscriptionStore.hasFeature(feature)
  }

  /** Get the minimum tier required for a feature */
  const requiredTierForFeature = (feature: string): PlanTier => {
    return FEATURE_TIER_MAP[feature] ?? 'grow'
  }

  /** Get the display name for a tier */
  const tierDisplayName = (tier: PlanTier): string => {
    const names: Record<PlanTier, string> = {
      grow: 'Grow',
      scale: 'Scale',
      pro: 'Pro',
      legacy: 'Legacy',
    }
    return names[tier] ?? tier
  }

  /** Check if a feature is locked (user doesn't have access and it's not a grow feature).
   *  Trial users with a subscription are restricted to their chosen tier's features. */
  const isFeatureLocked = (feature: string): boolean => {
    if (subscriptionStore.isLegacy) return false
    return !canAccessFeature(feature)
  }

  /** Get upgrade message for a locked feature */
  const upgradeMessage = (feature: string): string => {
    const tier = requiredTierForFeature(feature)
    return `Upgrade to ${tierDisplayName(tier)} to unlock this feature.`
  }

  return {
    canAccessFeature,
    requiredTierForFeature,
    tierDisplayName,
    isFeatureLocked,
    upgradeMessage,
    planTier: computed(() => subscriptionStore.planTier),
    isLegacy: computed(() => subscriptionStore.isLegacy),
  }
}
